import { HTMLProps, ReactNode, Ref, forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';

import { focusRing, pointerCursor } from '@src/theme/mixins';

// The props that are shared by both the regular Button and the LinkButton components.
type BaseButtonProps = {
  /**
   * The importance of the button. There should be only one primary button
   * per button grouping. The default variant is 'default'.
   */
  variant?: 'default' | 'primary' | 'disabled';
  /** The background color that the button is on. The default background is 'light'. */
  background?: 'light' | 'primary' | 'dark';
  /**
   * Use to add an icon to the button. Pass the icon as JSX, e.g., `<SomeIcon />`.
   */
  icon?: ReactNode;
  /**
   * If adding an icon, usin this prop to control where the icon is displayed.
   * The default position is 'start'.
   */
  iconPosition?: 'start' | 'end';
  round?: boolean;
  compact?: boolean;
};

const StyledButton = styled.button<{
  $round?: BaseButtonProps['round'];
  $compact?: BaseButtonProps['compact'];
  background: BaseButtonProps['background'];
  variant: BaseButtonProps['variant'];
}>`
  /* Base values: background: light; variant: default */
  --button-border-color: var(--color-gray-4);
  --button-bg-color: var(--color-gray-4);
  --button-text-color: var(--color-navy-3);
  --button-hover-bg-color: var(--color-gray-3);
  --button-hover-text-color: var(--color-navy-3);

  ${({ $round }) =>
    $round &&
    css`
      border-radius: var(--radius-full);
    `}

  ${({ variant }) =>
    variant === 'disabled' &&
    css`
      display: none;
    `}

  ${({ background, variant }) =>
    background === 'light' &&
    variant === 'primary' &&
    css`
      --button-border-color: var(--color-primary);
      --button-bg-color: var(--color-primary);
      --button-text-color: var(--color-black);
      --button-hover-bg-color: transparent;
      --button-hover-text-color: var(--color-black);
    `}

  ${({ background, variant }) =>
    background === 'primary' &&
    variant === 'default' &&
    css`
      --button-border-color: var(--color-black);
      --button-bg-color: transparent;
      --button-text-color: var(--color-black);
      --button-hover-bg-color: var(--color-black);
      --button-hover-text-color: var(--color-primary);
    `}

  ${({ background, variant }) =>
    background === 'primary' &&
    variant === 'primary' &&
    css`
      --button-border-color: var(--color-black);
      --button-bg-color: var(--color-black);
      --button-text-color: var(--color-primary);
      --button-hover-bg-color: transparent;
      --button-hover-text-color: var(--color-black);
    `}

    ${({ background, variant }) =>
      background === 'dark' &&
      variant === 'default' &&
      css`
      --button-border-color: var(--color-primary);
      --button-bg-color: transparent;
      --button-text-color: var(--color-white);
      --button-hover-bg-color: var(--color-primary);
      --button-hover-text-color: var(--color-black);
    `}

  ${({ background, variant }) =>
    background === 'dark' &&
    variant === 'primary' &&
    css`
      --button-border-color: var(--color-primary);
      --button-bg-color: var(--color-primary);
      --button-text-color: var(--color-black);
      --button-hover-bg-color: transparent;
      --button-hover-text-color: var(--color-primary);
    `}

  appearance: none;
  outline: none;
  position: relative;
  background: var(--button-bg-color);
  ${({ $compact }) =>
    $compact
      ? css`
          padding: var(--space-1) var(--space-3);
        `
      : css`
          padding: var(--space-3) var(--space-6);
        `};
  border: 2px solid var(--button-border-color);
  color: var(--button-text-color);
  font-size: var(--font-sm);
  line-height: var(--space-5);
  font-weight: var(--font-weight-bold);
  /** Allow the button to shrink if its in a flexbox: */
  flex-shrink: 1;
  text-decoration: none;
  white-space: nowrap;
  text-align: center;
  user-select: none;
  transition-property: color, background-color, border-color;
  transition-duration: 150ms;

  ${focusRing}
  ${pointerCursor}

  &[disabled],
  &[aria-disabled='true'] {
    opacity: 0.5;
  }

  :not([disabled]),
  :not([aria-disabled='true']) {
    :hover,
    [aria-expanded='true'] {
      background-color: var(--button-hover-bg-color);
      color: var(--button-hover-text-color);
    }

    :active,
    [data-active='true'] {
      background-color: var(--button-bg-color);
      color: var(--button-text-color);
    }
  }

  /* Ensure an icon by itself creates the correct button height. */
  svg {
    margin: var(--space-0-5) 0;
    height: var(--space-6);
  }

  /*
  Grommet icons in react-icons have hard-coded color values on the paths.
  https://github.com/react-icons/react-icons/issues/334
  */
  svg path,
  svg g {
    stroke: currentColor;
  }
`;

// Required to wrap the button contents in when displaying an icon.
const ButtonContentWrapper = styled.span`
  display: flex;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
`;

// Required to wrap the button text in when displaying an icon.
const ButtonText = styled.span`
  display: inline-block;
`;

type ButtonContentProps = Pick<BaseButtonProps, 'icon' | 'iconPosition'> & { children?: ReactNode };

// The shared content markup for the standard and link buttons.
function ButtonContent({ icon, iconPosition = 'start', children }: ButtonContentProps) {
  return (
    <>
      {icon && (
        <ButtonContentWrapper>
          {iconPosition === 'start' && icon}
          {children && <ButtonText>{children}</ButtonText>}
          {iconPosition === 'end' && icon}
        </ButtonContentWrapper>
      )}
      {!icon && children}
    </>
  );
}

const defaultVariant = 'default';
const defaultBackground = 'light';

// Pick<HTMLProps<HTMLElement>, 'role' | 'aria-label' | 'aria-labelledby'>;
// biome-ignore lint/suspicious/noExplicitAny: TODO: fix
export type ButtonProps = BaseButtonProps & Omit<HTMLProps<HTMLElement>, 'children'> & { children?: any };

export const Button = forwardRef(
  (
    {
      variant = defaultVariant,
      background = defaultBackground,
      icon,
      iconPosition,
      children,
      round,
      compact,
      ...rest
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => (
    // @ts-expect-error
    <StyledButton ref={ref} variant={variant} background={background} $round={round} $compact={compact} {...rest}>
      <ButtonContent icon={icon} iconPosition={iconPosition}>
        {children}
      </ButtonContent>
    </StyledButton>
  ),
);

export type LinkButtonProps = BaseButtonProps & Omit<LinkProps, 'type'> & { children?: ReactNode };

/** A Link component from the react-router-dom library that is styled to look like a button. */
export function LinkButton({
  variant = defaultVariant,
  background = defaultBackground,
  icon,
  iconPosition,
  children,
  round,
  compact,
  ...rest
}: LinkButtonProps) {
  return (
    <StyledButton variant={variant} background={background} $round={round} $compact={compact} {...rest} as={Link}>
      <ButtonContent icon={icon} iconPosition={iconPosition}>
        {children}
      </ButtonContent>
    </StyledButton>
  );
}
